import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { Location } from "react-router-dom";

import {
  AccountDetailPage,
  AccountFormPage,
  AccountListPage,
  PlanYearDetailPage,
} from "@/features/accounts";
import { PermissionGuard, ProtectedRoute } from "@/features/auth";
import {
  CarrierDetailPage,
  CarriersListPage,
} from "@/features/carriers";
import {
  CommitmentDetailModal,
  CommitmentDetailPage,
  CommitmentFormPage,
  CommitmentTrackerPage,
} from "@/features/commitments";
import {
  PlaybookEditorAliasRedirect,
  PlaybookEditorPage,
  PlaybookListPage,
} from "@/features/playbooks";
import {
  AcceptInvitationPage,
  SettingsPage,
} from "@/features/settings";
import {
  ContactDetailPage,
  OpportunitiesListPage,
  OpportunityDetailPage,
  ProspectDetailPage,
  SalesCrmPage,
  SalesImportPage,
} from "@/features/sales";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { env } from "@/lib/env";
import { Dashboard } from "@/pages/Dashboard";
import { Login } from "@/pages/Login";
import { ResetPassword } from "@/pages/ResetPassword";
import { Signup } from "@/pages/Signup";


/** Location-state contract for opening a Commitment detail as a
 * route-overlay modal (Commitments-Modal-01). Surfaces (Tracker
 * row, Kanban card, Account commitments panel, etc.) stamp this
 * onto the ``Link``'s ``state`` so the router knows to render the
 * background route and overlay the modal route on top. */
export interface CommitmentBackgroundLocationState {
  backgroundLocation?: Location;
  /** Optional surface-side hints carried alongside the background
   * pointer so the detail page's back link / breadcrumb can read
   * them when it falls back to the page surface. */
  fromUrl?: string;
  fromLabel?: string;
}

function MarketingRedirect() {
  useEffect(() => {
    const marketingUrl = new URL(env.marketingOrigin);
    if (window.location.origin === marketingUrl.origin) {
      window.location.replace("/dashboard");
      return;
    }
    window.location.replace(marketingUrl.toString());
  }, []);
  return null;
}

export default function App() {
  const location = useLocation();
  const state = location.state as CommitmentBackgroundLocationState | null;
  // ``backgroundLocation`` is the URL the user was on when they
  // clicked a Commitment link with the modal-overlay state. The
  // app renders its routes against *that* location (so the
  // originating page stays mounted and visible behind the modal),
  // while a parallel ``Routes`` block renders the modal route on
  // top. Direct URL hits / refreshes don't carry the state, so
  // they fall through to the canonical full-page detail route.
  const backgroundLocation = state?.backgroundLocation;
  return (
    <>
      <Routes location={backgroundLocation ?? location}>
        <Route path="/" element={<MarketingRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        {/* Public — Settings-02 password reset confirmation. Lives
            outside ProtectedRoute because the link is reached from
            email; the token is the auth boundary. */}
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Public — Settings-01b accept-invitation surface. Lives outside
            ProtectedRoute so an invitee without an account can land here
            before logging in. */}
        <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            {/* Placeholder pages — surfaces with a sidebar promise but
                no implementation yet. They render a "Coming soon"
                card that explains what will land there. BeneAI used
                to live here; it now opens as a side drawer from the
                sidebar launcher and never owns a route. */}
            {/* Settings-03 — Sales CRM is owner/admin/sales OR
                producer. Hidden from the sidebar for everyone else;
                the guard catches direct URL hits + role changes
                that race the sidebar render. */}
            <Route
              path="/dashboard/sales-crm"
              element={
                <PermissionGuard permission="can_access_sales_crm">
                  <SalesCrmPage />
                </PermissionGuard>
              }
            />
            {/* FE-SCRM-052 — flat Opportunities list. Peer surface
                to the Renewal Board, NOT a sub-tab. Reached from the
                Renewal Board's overflow link and Prospect detail's
                "View all" affordance. */}
            <Route
              path="/dashboard/sales-crm/opportunities"
              element={
                <PermissionGuard permission="can_access_sales_crm">
                  <OpportunitiesListPage />
                </PermissionGuard>
              }
            />
            <Route
              path="/dashboard/sales-crm/opportunities/:opportunityId"
              element={
                <PermissionGuard permission="can_access_sales_crm">
                  <OpportunityDetailPage />
                </PermissionGuard>
              }
            />
            <Route
              path="/dashboard/sales-crm/prospects/:prospectId"
              element={
                <PermissionGuard permission="can_access_sales_crm">
                  <ProspectDetailPage />
                </PermissionGuard>
              }
            />
            <Route
              path="/dashboard/sales-crm/import"
              element={
                <PermissionGuard permission="can_access_sales_crm">
                  <SalesImportPage />
                </PermissionGuard>
              }
            />
            <Route
              path="/dashboard/sales-crm/contacts/:contactId"
              element={
                <PermissionGuard permission="can_access_sales_crm">
                  <ContactDetailPage />
                </PermissionGuard>
              }
            />
            <Route path="/dashboard/accounts" element={<AccountListPage />} />
            {/* ``/new`` ahead of ``/:accountId`` so it matches the static
                path; ``/:accountId/edit`` ahead of ``/:accountId`` so the
                edit segment doesn't collapse into the detail route. */}
            <Route path="/dashboard/accounts/new" element={<AccountFormPage mode="create" />} />
            <Route path="/dashboard/accounts/:accountId/edit" element={<AccountFormPage mode="edit" />} />
            <Route path="/dashboard/accounts/:accountId/plan_years/:planYearId" element={<PlanYearDetailPage />} />
            <Route path="/dashboard/accounts/:accountId" element={<AccountDetailPage />} />
            <Route path="/dashboard/commitment-tracker" element={<CommitmentTrackerPage />} />
            {/* FE-043: ``/new`` ahead of ``/:commitmentId`` so static
                path wins; ``/:commitmentId/edit`` ahead of
                ``/:commitmentId`` so the edit segment doesn't collapse
                into the detail route. */}
            <Route path="/dashboard/commitments/new" element={<CommitmentFormPage mode="create" />} />
            <Route path="/dashboard/commitments/:commitmentId/edit" element={<CommitmentFormPage mode="edit" />} />
            {/* Direct URL load / refresh hits the full-page route.
                When opened with ``state.backgroundLocation`` the
                router shows the background page underneath and
                renders ``CommitmentDetailModal`` from the overlay
                ``Routes`` block below instead. */}
            <Route path="/dashboard/commitments/:commitmentId" element={<CommitmentDetailPage />} />
            {/* FE-045: /edit is canonical; /editor aliases for FE-044 links. */}
            {/* FE-SCRM-030: Carrier library. Read access is open to
                any authenticated entity member; write actions inside
                the page gate on ``can_manage_carriers`` (owner/admin). */}
            <Route path="/dashboard/carriers" element={<CarriersListPage />} />
            <Route
              path="/dashboard/carriers/:carrierId"
              element={<CarrierDetailPage />}
            />
            <Route path="/dashboard/playbooks" element={<PlaybookListPage />} />
            <Route path="/dashboard/playbooks/:playbookId/edit" element={<PlaybookEditorPage />} />
            <Route path="/dashboard/playbooks/:playbookId/editor" element={<PlaybookEditorAliasRedirect />} />
            <Route path="/dashboard/settings" element={<SettingsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Modal-overlay routes — only render when a background
          location is present. The modal mounts on top of whatever
          the background route rendered; closing the modal pops
          history back to the background URL so list filters /
          tabs / scroll on the originating surface stay intact. */}
      {backgroundLocation && (
        <Routes>
          <Route
            path="/dashboard/commitments/:commitmentId"
            element={<CommitmentDetailModal />}
          />
        </Routes>
      )}
    </>
  );
}
